import { useState, useEffect } from 'react';
import { fetchIngestionFailures } from '../../api';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatReason(reason) {
  const labels = {
    'Invalid JPEG: missing SOI/EOI markers': 'Truncated JPEG',
    'magic_bytes_invalid': 'Invalid file format',
    'size_too_small': 'File too small',
    'exiftool_error': 'RAW integrity error',
  };
  return labels[reason] || reason;
}

function formatTime(isoString) {
  if (!isoString) return '';
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function IngestionFailureList({ onClose }) {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchIngestionFailures()
      .then((data) => {
        if (!cancelled) {
          setFailures(data.results || data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading failures…</div>
      </div>
    );
  }

  if (failures.length === 0) {
    return (
      <div style={styles.container}>
        <div style={styles.empty}>No unresolved ingestion failures.</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <span style={styles.title}>Failed Uploads ({failures.length})</span>
        {onClose && (
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        )}
      </div>
      <div style={styles.list}>
        {failures.map((f) => (
          <div key={f.id} style={styles.row}>
            <div style={styles.filename}>{f.filename}</div>
            <div style={styles.meta}>
              {f.photographer_username && (
                <span style={styles.tag}>{f.photographer_username}</span>
              )}
              <span style={styles.tag}>{formatBytes(f.file_size)}</span>
              <span style={styles.reason}>{formatReason(f.failure_reason)}</span>
              <span style={styles.time}>{formatTime(f.created_at)}</span>
            </div>
            {f.failure_details && (
              <div style={styles.details}>{f.failure_details}</div>
            )}
            {f.resolved && (
              <div style={styles.resolved}>
                ✓ Re-uploaded successfully
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

const styles = {
  container: {
    background: 'rgba(10, 19, 26, 0.9)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    padding: '12px 16px',
    marginTop: -6,
    marginBottom: 14,
    maxHeight: 300,
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: 600,
    color: '#ff6b6b',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    color: 'var(--muted)',
    cursor: 'pointer',
    fontSize: 14,
    padding: '2px 6px',
  },
  loading: {
    fontSize: 13,
    color: 'var(--muted)',
    padding: '8px 0',
  },
  empty: {
    fontSize: 13,
    color: 'var(--muted)',
    padding: '8px 0',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  row: {
    padding: '8px 10px',
    background: 'rgba(255, 255, 255, 0.03)',
    borderRadius: 6,
    borderLeft: '3px solid #ff6b6b',
  },
  filename: {
    fontSize: 13,
    fontWeight: 600,
    color: '#ccc',
    fontFamily: 'monospace',
  },
  meta: {
    display: 'flex',
    gap: 8,
    marginTop: 4,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  tag: {
    fontSize: 11,
    color: 'var(--muted)',
    background: 'rgba(255,255,255,0.06)',
    padding: '1px 6px',
    borderRadius: 4,
  },
  reason: {
    fontSize: 11,
    color: '#ff6b6b',
    fontWeight: 500,
  },
  time: {
    fontSize: 11,
    color: 'var(--muted)',
  },
  details: {
    fontSize: 11,
    color: 'var(--muted)',
    marginTop: 4,
    fontStyle: 'italic',
  },
  resolved: {
    fontSize: 11,
    color: '#66ffb7',
    marginTop: 4,
    fontWeight: 500,
  },
};
